// RegistrationTab.jsx
import React, { useState } from 'react';
import DocumentViewerModal from './DocumentViewerModal';
import '../../styles/components/admin/DoctorRegistrationTab.css';

import personalIcon from '../../assets/icons/indicators/PersonalInfo_icon.png';

const RegistrationTab = ({ doctor }) => {
  // Document viewer modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentDocument, setCurrentDocument] = useState(null);

  // 🔧 SAFE: Helper function to safely render any value
  const safeRender = (value, fallback = 'Not provided') => {
    if (value === null || value === undefined || value === '') {
      return fallback;
    }
    
    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        return value.length > 0 ? value.join(', ') : fallback;
      }
      return JSON.stringify(value);
    }
    
    return String(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const getVerificationStatusClass = (status) => {
    if (!status) return 'pending';
    const statusLower = status.toLowerCase();
    if (statusLower === 'approved') return 'approved';
    if (statusLower === 'rejected') return 'rejected';
    return 'pending';
  };

  const getVerificationStatusText = (status) => {
    if (!status) return 'PENDING';
    return status.toUpperCase();
  };

  // Helper to render verification notes safely
  const renderVerificationNotes = (notes) => {
    if (!notes) return null;
    
    // If notes is an array
    if (Array.isArray(notes)) {
      return notes.map((note, index) => {
        // If note is an object with content or note property
        if (typeof note === 'object') {
          return <div key={index}>{note.note || note.content || JSON.stringify(note)}</div>;
        }
        // If note is a string
        return <div key={index}>{note}</div>;
      });
    }
    
    // If notes is an object
    if (typeof notes === 'object' && notes !== null) {
      return <div>{notes.note || notes.content || JSON.stringify(notes)}</div>;
    }
    
    // If notes is a string
    return <div>{notes}</div>;
  };

  // Document viewer functions
  const openDocumentModal = (url, title) => {
    if (!url) {
      console.error('Document not available');
      return;
    }
    
    setCurrentDocument({
      url: url,
      title: title,
      type: getDocumentType(url)
    });
    setIsModalOpen(true);
  };

  const closeDocumentModal = () => {
    setIsModalOpen(false);
    setCurrentDocument(null);
  };

  const getDocumentType = (url) => {
    if (!url) return 'unknown';
    
    const extension = url.split('.').pop().toLowerCase();
    if (['pdf'].includes(extension)) return 'pdf';
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension)) return 'image';
    if (['doc', 'docx'].includes(extension)) return 'document';
    return 'unknown';
  };

  const extractFilenameFromUrl = (url) => {
    if (!url) return 'Document';
    
    // Extract filename from Cloudinary URL
    try {
      // Get the last part of the URL (after the last slash)
      const fullFilename = url.split('/').pop();
      const parts = fullFilename.split('_');
      if (parts.length > 1) {
        return parts.slice(parts.length - 1).join('_');
      }
      
      // Fallback to the full filename
      return fullFilename;
    } catch (err) {
      console.error('Error extracting filename:', err);
      return 'Document';
    }
  };

  const renderDocumentLink = (url, label) => {
    if (!url) return <span className="reg-no-document">Not provided</span>;
    
    return (
      <div className="reg-document-container">
        <div className="reg-document-info">
          <span className="reg-document-name">{label}</span>
          <span className="reg-document-type">{getDocumentType(url).toUpperCase()}</span>
        </div>
        <div className="reg-document-actions">
          <button 
            className="reg-view-button"
            onClick={() => openDocumentModal(url, label)}
            title="View Document in Modal"
          >
            <svg className="reg-view-icon" viewBox="0 0 24 24" fill="none">
              <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor"/>
            </svg>
            View
          </button>
        </div>
      </div>
    );
  };

  const renderAvailabilitySchedule = (availability) => {
    if (!availability) return <p className="reg-no-data">No availability information provided</p>;
    
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const dayLabels = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    
    const availableDays = days.filter(day => availability[day]?.available);
    
    if (availableDays.length === 0) {
      return <p className="reg-no-data">No available days set</p>;
    }
    
    return (
      <div className="reg-availability-schedule">
        {days.map((day, index) => {
          const dayData = availability[day];
          if (!dayData?.available) return null;
          
          return (
            <div key={day} className="reg-availability-day">
              <div className="reg-day-name">{dayLabels[index]}</div>
              <div className="reg-time-slots">
                {dayData.slots && dayData.slots.length > 0 ? (
                  dayData.slots.map((slot, slotIndex) => (
                    <div key={slotIndex} className="reg-time-slot">
                      <span className="reg-start-time">{safeRender(slot.startTime, 'Not set')}</span>
                      <span className="reg-time-separator"> - </span>
                      <span className="reg-end-time">{safeRender(slot.endTime, 'Not set')}</span>
                    </div>
                  ))
                ) : (
                  <div className="reg-time-slot">
                    <span className="reg-no-times">No specific times set</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="registration-tab">
      {/* Personal Information Section */}
      <div className="reg-section">
        <h3 className="reg-section-title">
          <img src={personalIcon} alt="" className="reg-section-icon" />
          Personal Information
        </h3>
        <div className="reg-section-content">
          <div className="reg-info-grid">
            <div className="reg-info-item">
              <label className="reg-label">First Name</label>
              <div className="reg-value">{safeRender(doctor.firstName)}</div>
            </div>
            
            <div className="reg-info-item">
              <label className="reg-label">Last Name</label>
              <div className="reg-value">{safeRender(doctor.lastName)}</div>
            </div>
            
            <div className="reg-info-item">
              <label className="reg-label">Email Address</label>
              <div className="reg-value">{safeRender(doctor.email)}</div>
            </div>
            
            <div className="reg-info-item">
              <label className="reg-label">Personal Contact Number</label>
              <div className="reg-value">{safeRender(doctor.personalContactNumber || doctor.phoneNumber)}</div>
            </div>
            
            <div className="reg-info-item">
              <label className="reg-label">Date of Birth</label>
              <div className="reg-value">{formatDate(doctor.dateOfBirth)}</div>
            </div>
            
            <div className="reg-info-item">
              <label className="reg-label">Gender</label>
              <div className="reg-value">{safeRender(doctor.gender)}</div>
            </div>

            <div className="reg-info-item">
              <label className="reg-label">Clinic Location</label>
              <div className="reg-value">{safeRender(doctor.clinicLocation || doctor.clinicAddress)}</div>
            </div>
            
            <div className="reg-info-item">
              <label className="reg-label">Clinic Contact Number</label>
              <div className="reg-value">{safeRender(doctor.clinicContactNumber)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Professional Information Section */}
      <div className="reg-section">
        <h3 className="reg-section-title">Professional Information</h3>
        <div className="reg-section-content">
          <div className="reg-info-grid">
            <div className="reg-info-item">
              <label className="reg-label">Specialty</label>
              <div className="reg-value">{safeRender(doctor.specialty || doctor.specialization)}</div>
            </div>

            <div className="reg-info-item">
              <label className="reg-label">Professional Title</label>
              <div className="reg-value">{safeRender(doctor.title)}</div>
            </div>

            <div className="reg-info-item">
              <label className="reg-label">Areas of Expertise</label>
              <div className="reg-value">{safeRender(doctor.areasOfExpertise)}</div>
            </div>
            
            <div className="reg-info-item">
              <label className="reg-label">Experience</label>
              <div className="reg-value">{safeRender(doctor.experience || doctor.yearsOfExperience || doctor.yearsOfPractice)}</div>
            </div>

            <div className="reg-info-item">
              <label className="reg-label">License Number</label>
              <div className="reg-value">{safeRender(doctor.licenseNumber)}</div>
            </div>

            <div className="reg-info-item">
              <label className="reg-label">License Expiry Date</label>
              <div className="reg-value">{formatDate(doctor.licenseExpiryDate)}</div>
            </div>
            
            <div className="reg-info-item full-width">
              <label className="reg-label">Professional Bio</label>
              <div className="reg-value reg-bio">{safeRender(doctor.bio, 'No bio provided')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Service Availability Section */}
      <div className="reg-section">
        <h3 className="reg-section-title">Availability</h3>
        <div className="reg-section-content">
          <div className="reg-availability-info">
            <div className="reg-schedule-section">
              <label className="reg-schedule-label">Weekly Schedule</label>
              {renderAvailabilitySchedule(doctor.availability)}
            </div>
            
            <div className="reg-schedule-section">
              <label className="reg-schedule-label">Appointment Types</label>
              <div className="reg-service-types">
                <div className="reg-service-type">
                  <span className="reg-service-badge">
                    {doctor.inPerson || doctor.appointmentTypes?.inPerson ? 'In-person, Telehealth' : 'Telehealth only'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Credentials Section */}
      <div className="reg-section">
        <h3 className="reg-section-title">Credentials</h3>
        <div className="reg-section-content">
          {/* Education */}
          {doctor.education && doctor.education.length > 0 && (
            <div className="reg-credentials-subsection">
              <h4 className="reg-credentials-subtitle">Education</h4>
              <div className="reg-credentials-list">
                {doctor.education.map((edu, index) => (
                  <div key={index} className="reg-credential-item">
                    <span className="reg-credential-degree">{safeRender(edu.degree, 'Degree not specified')}</span>
                    <div className="reg-credential-institution-year">
                      <span className="reg-credential-institution">{safeRender(edu.institution, 'Institution not specified')}</span>
                      <span className="reg-credential-year">{safeRender(edu.year, 'Year not specified')}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Licenses */}
          <div className="reg-credentials-subsection">
            <h4 className="reg-credentials-subtitle">Licenses</h4>
            <div className="reg-credential-item">
              <span className="reg-credential-degree">
                State Psychology License License #: {safeRender(doctor.licenseNumber)}
              </span>
              <span className="reg-credential-year">
                Expires: {formatDate(doctor.licenseExpiryDate)}
              </span>
            </div>
          </div>

          {/* Certifications */}
          {doctor.certifications && doctor.certifications.length > 0 && (
            <div className="reg-credentials-subsection">
              <h4 className="reg-credentials-subtitle">Certifications</h4>
              <div className="reg-credentials-list">
                {doctor.certifications.map((cert, index) => (
                  <div key={index} className="reg-credential-item">
                    <span className="reg-credential-degree">{safeRender(cert.degree || cert.name, 'Certification not specified')}</span>
                    <div className="reg-credential-institution-year">
                      <span className="reg-credential-institution">{safeRender(cert.issuingAuthority || cert.authority, 'Authority not specified')}</span>
                      <span className="reg-credential-year">{safeRender(cert.year, 'Year not specified')}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Document Verification Section */}
          <div className="reg-document-divider"></div>
          
          <div className="reg-credentials-subsection">
            <h4 className="reg-credentials-subtitle">Document Verification</h4>
            <div className="reg-documents-grid">
              <div className="reg-document-item">
                <label className="reg-label">License Document</label>
                {renderDocumentLink(doctor.licenseDocumentUrl, extractFilenameFromUrl(doctor.licenseDocumentUrl) || 'License Document')}
              </div>
              <div className="reg-document-item">
                <label className="reg-label">Education Certificate</label>
                {renderDocumentLink(doctor.educationCertificateUrl, extractFilenameFromUrl(doctor.educationCertificateUrl) || 'Education Certificate')}
              </div>
              <div className="reg-document-item">
                <label className="reg-label">Diploma</label>
                {renderDocumentLink(doctor.diplomaUrl || doctor.educationCertificateUrl, extractFilenameFromUrl(doctor.diplomaUrl || doctor.educationCertificateUrl) || 'Diploma')}
              </div>
              <div className="reg-document-item">
                <label className="reg-label">Resume/CV</label>
                {renderDocumentLink(doctor.resumeUrl || doctor.additionalDocumentUrls?.[0], extractFilenameFromUrl(doctor.resumeUrl || doctor.additionalDocumentUrls?.[0]) || 'Resume/CV')}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Verification Status Section */}
      <div className="reg-section">
        <h3 className="reg-section-title">Verification Status</h3>
        <div className="reg-section-content">
          <div className="reg-info-grid">
            <div className="reg-info-item">
              <label className="reg-label">Verification Status</label>
              <div className="reg-value">
                <span className={`reg-status-badge ${getVerificationStatusClass(doctor.verificationStatus)}`}>
                  {getVerificationStatusText(doctor.verificationStatus)}
                </span>
              </div>
            </div>
            
            <div className="reg-info-item">
              <label className="reg-label">Verification Date</label>
              <div className="reg-value">{formatDate(doctor.verificationDate)}</div>
            </div>
            
            <div className="reg-info-item">
              <label className="reg-label">Registration Date</label>
              <div className="reg-value">{formatDate(doctor.createdAt || doctor.registrationDate)}</div>
            </div>
            
            <div className="reg-info-item">
              <label className="reg-label">Account Status</label>
              <div className="reg-value">
                <span className={`reg-account-status ${doctor.accountStatus || 'active'}`}>
                  {doctor.accountStatus === 'inactive' ? 'INACTIVE' : 'ACTIVE'}
                </span>
              </div>
            </div>
            
            {doctor.verificationNotes && (
              <div className="reg-info-item full-width">
                <label className="reg-label">Verification Notes</label>
                <div className="reg-value reg-notes">
                  {renderVerificationNotes(doctor.verificationNotes)}
                </div>
              </div>
            )}
            
            {doctor.rejectionReason && (
              <div className="reg-info-item full-width">
                <label className="reg-label">Rejection Reason</label>
                <div className="reg-value reg-rejection">{doctor.rejectionReason}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Profile Picture Section */}
      {doctor.profilePicture && (
        <div className="reg-section">
          <h3 className="reg-section-title">Profile Picture</h3>
          <div className="reg-section-content">
            <div className="reg-profile-picture">
              <img src={doctor.profilePicture} alt={`${doctor.firstName} ${doctor.lastName}`} />
            </div>
          </div>
        </div>
      )}

      {/* Document Viewer Modal */}
      {isModalOpen && currentDocument && (
        <DocumentViewerModal
          isOpen={isModalOpen}
          onClose={closeDocumentModal}
          document={currentDocument}
        />
      )}
    </div>
  );
};

export default RegistrationTab;